import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
	filterByConfidence,
	hasConfidence,
	indexTracks,
	lineCrossings,
	lineFromAnalysis,
	minSensitivity,
	sizeHistogram,
	sizesUpTo,
} from './tracksModel';
import { bodySizes } from './flightModel';
import { applyCorrections, correctionsSummary } from './corrections';

/**
 * Everything the count panel shows, computed in the browser from tracks.json: the count for the chosen mode at the
 * current frame, its total, the series over time, in-view numbers and the size histogram. The stored statistics stay
 * the official totals; a count from a moved line is labelled as recalculated.
 */

const sameLine = (a, b) =>
	a &&
	b &&
	a.start[0] === b.start[0] &&
	a.start[1] === b.start[1] &&
	a.end[0] === b.end[0] &&
	a.end[1] === b.end[1];

// flight: indexFlight(telemetry.json) when the video has flight data; body sizes are then in metres, else pixels
// layer: the corrections layer (people's edits); applied after the sensitivity filter, so what someone added stays
const useCountModel = ({ tracksDoc, statistics, virtualLine, flight = null, layer = null }) => {
	// Sensitivity: hide tracks whose best detection is below this confidence (never below the job's threshold)
	const floor = minSensitivity(tracksDoc);
	const [sensitivity, setSensitivity] = useState(floor);
	useEffect(() => setSensitivity(floor), [floor, tracksDoc]);
	const deferred = useDeferredValue(sensitivity);
	const filtered = deferred > floor + 1e-9;
	// people's edits: the stored totals no longer hold once there are any
	const changes = useMemo(() => correctionsSummary(tracksDoc, layer), [tracksDoc, layer]);
	const corrected = changes.added + changes.removed + changes.merged > 0;
	const recount = filtered || corrected;
	const index = useMemo(
		() =>
			tracksDoc
				? indexTracks(applyCorrections(filterByConfidence(tracksDoc, deferred), layer))
				: null,
		[tracksDoc, deferred, layer]
	);
	const method = statistics?.counting_method_used || tracksDoc?.counting_method;
	const officialLine = useMemo(
		() => (index ? lineFromAnalysis(virtualLine, index) : null),
		[index, virtualLine]
	);
	const [mode, setMode] = useState(method === 'line_based' ? 'line' : 'hover');
	const [line, setLine] = useState(officialLine);
	const [frame, setFrame] = useState(0);

	useEffect(() => setLine(officialLine), [officialLine]);
	useEffect(() => setMode(method === 'line_based' ? 'line' : 'hover'), [method]);

	const lineStats = useMemo(
		() => (index && line ? lineCrossings(index, line) : null),
		[index, line]
	);
	const lineMoved = !sameLine(line, officialLine) || method !== 'line_based';
	const sizes = useMemo(() => (index ? bodySizes(index, flight) : null), [index, flight]);
	const hist = useMemo(
		() => ({
			...sizeHistogram(index ? sizesUpTo(index, frame, sizes) : [], sizes?.unit),
			across: !!sizes?.across,
			heightRef: flight?.doc?.height_ref || null,
			flightWithoutHeight: !!flight && !flight.hasFootprint,
		}),
		[index, frame, sizes, flight]
	);

	if (!index) return { index: null, setFrame };
	const last = index.nFrames - 1;
	const f = Math.min(frame, last);

	let count;
	let subtitle;
	let note = null;
	let noteWarn = false;
	let series;
	if (mode === 'line') {
		count = lineStats?.netSeries[f] ?? 0;
		const total =
			!lineMoved && !recount && statistics?.line_cross_net != null
				? statistics.line_cross_net
				: lineStats?.net;
		subtitle = `passed the line so far · ${total ?? '—'} in total`;
		series = lineStats?.netSeries;
		if (lineMoved) {
			note =
				method === 'line_based'
					? 'Recalculated for this line (the stored count used the line drawn before processing)'
					: 'Counted across this line in the browser (the job itself counted every animal, not a line)';
			noteWarn = method === 'line_based';
		} else
			note = `${(!recount && statistics?.line_cross_count) || lineStats?.events || '—'} crossing events, both directions`;
	} else if (mode === 'max') {
		count = index.inView[f];
		subtitle = `in view now · most at once ${index.maxInView}`;
		series = index.inView;
	} else {
		count = index.uniqueSeries[f];
		const total =
			!recount && statistics?.unique_count != null
				? statistics.unique_count
				: index.uniqueSeries[last];
		subtitle = `counted so far · ${total} in total`;
		note = 'Each animal counts once, after it has been tracked for 1 s';
		series = index.uniqueSeries;
	}

	if (filtered && !noteWarn)
		note = `Counted at sensitivity ${Math.round(deferred * 100)} % in the browser (the stored count used ${Math.round(floor * 100)} %)`;

	return {
		index,
		method,
		sensitivity: {
			value: sensitivity,
			min: floor,
			available: hasConfidence(tracksDoc),
			onChange: setSensitivity,
		},
		mode,
		setMode,
		changes,
		line,
		setLine,
		officialLine,
		lineMoved,
		frame: f,
		setFrame,
		count,
		subtitle,
		note,
		noteWarn,
		series,
		hist,
	};
};

export default useCountModel;
